import { transaction } from 'mobx';

import { getQuantityRemaining } from 'shared/helpers/products';
import type { Products, Dispensaries } from 'src/gql/graphql';
import { parseProduct, Product } from 'utils/helpers/product';

type CartItem = {
  id: string;
  quantity: number;
  option: string;
  additionalOption?: string;
  name?: string;
};

type CartItemToAdd = {
  product: Product;
  quantity: number;
  option: string;
  additionalOption?: string;
  trackerSource: string;
};

type OrderData = {
  products?: Products[];
  cart?: {
    details?: {
      products?: CartItem[];
    };
  };
  customer?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    phone?: string;
    id?: string;
  };
  medicalOrder?: boolean;
  paymentMethod?: string;
  deliveryOption?: string;
  utmData?: Record<string, any>;
};

type TrackedCart = {
  token: string;
  order?: OrderData;
};

type Cart = {
  clearOrder: () => void;
  checkoutToken: string | null;
  isMedical: boolean;
  order: {
    paymentMethod?: string;
    deliveryOption?: string;
  };
  utmData?: Record<string, unknown>;
  updateGuestCustomer: (key: string, value: unknown) => void;
  addItem: (dispensary: Dispensaries, item: CartItemToAdd) => void;
};

type User = {
  isLoggedIn: boolean;
};

export type FailedCartItem = {
  id: string;
  name?: string;
  reason: string;
  quantity: number;
  option: string;
  quantityAvailable?: number;
};

type CartRestorationResult = {
  restoredCount: number;
  failedCount: number;
  failedItems: FailedCartItem[];
};

export function getProductIdsFromOrderData(orderData: OrderData): string[] {
  const cartItems = orderData.cart?.details?.products ?? [];
  return Array.from(new Set(cartItems.map((item) => item.id).filter(Boolean)));
}

export function restoreCartFromTrackedData(cart: Cart, user: User, trackedCart: TrackedCart): void {
  transaction(() => {
    cart.clearOrder();
    cart.checkoutToken = trackedCart.token;

    const orderData = trackedCart.order;
    if (!orderData) {
      return;
    }

    if (orderData.medicalOrder !== undefined) {
      cart.isMedical = orderData.medicalOrder;
    }

    if (orderData.paymentMethod) {
      cart.order.paymentMethod = orderData.paymentMethod;
    }

    if (orderData.deliveryOption) {
      cart.order.deliveryOption = orderData.deliveryOption;
    }

    if (orderData.utmData) {
      cart.utmData = orderData.utmData;
    }

    if (orderData.customer && !user.isLoggedIn) {
      Object.entries(orderData.customer)
        .filter(([key, value]) => value && key !== 'id')
        .forEach(([key, value]) => {
          cart.updateGuestCustomer(key, value);
        });
    }
  });
}

export function restoreCartItems(
  cart: Cart,
  dispensary: Dispensaries,
  fetchedProducts: Products[],
  orderData: OrderData
): CartRestorationResult {
  const cartItems = orderData.cart?.details?.products ?? [];

  if (cartItems.length === 0) {
    return {
      restoredCount: 0,
      failedCount: 0,
      failedItems: [],
    };
  }

  let restoredCount = 0;
  let failedCount = 0;
  const failedItems: FailedCartItem[] = [];

  const productMap = new Map<string, Product>();
  fetchedProducts.forEach((rawProduct) => {
    const product = parseProduct(rawProduct);
    productMap.set(product.id ?? '', product);
  });

  const processingResults = cartItems.reduce(
    (acc, cartItem) => {
      try {
        const product = productMap.get(cartItem.id);
        const productName = product?.Name ?? getProductName(cartItem.id, orderData.products);

        // product not found, archived, or inactive
        if (!product || ['Archived', 'Inactive'].includes(product.Status ?? '')) {
          acc.failedItems.push({
            id: cartItem.id,
            name: productName,
            reason: 'Product no longer available',
            quantity: cartItem.quantity,
            option: cartItem.option,
            quantityAvailable: 0,
          });
          return acc;
        }

        // selected product option (e.g. 1g, 2g) is out of stock
        const availableOptions = Array.isArray(product.Options) ? product.Options.map(String) : [];
        if (!availableOptions.includes(String(cartItem.option))) {
          acc.failedItems.push({
            id: cartItem.id,
            name: productName,
            reason: `Option "${String(cartItem.option)}" no longer available`,
            quantity: cartItem.quantity,
            option: cartItem.option,
            quantityAvailable: 0,
          });
          return acc;
        }

        // fewer than the quantity in the cart is available
        let quantityToAdd = cartItem.quantity;
        const quantityAvailable = getQuantityRemaining(product, cartItem.option);
        if (quantityAvailable < cartItem.quantity) {
          quantityToAdd = quantityAvailable;
          acc.failedItems.push({
            id: cartItem.id,
            name: productName,
            reason: `Quantity "${String(cartItem.quantity)}" for "${String(cartItem.option)}" no longer available`,
            quantity: cartItem.quantity,
            option: cartItem.option,
            quantityAvailable,
          });
        } // here we don't return the acc because we still want to add the item to the cart

        const itemForCart = {
          product,
          quantity: Number(quantityToAdd),
          option: String(cartItem.option),
          additionalOption: cartItem.additionalOption ? String(cartItem.additionalOption) : undefined,
          trackerSource: 'cart_restoration',
        };

        acc.itemsToAdd.push({
          cartItem,
          itemForCart,
          product,
        });

        return acc;
      } catch (error) {
        console.error(`Failed to validate cart item ${String(cartItem.id)}:`, error);
        acc.failedItems.push({
          id: cartItem.id,
          reason: error instanceof Error ? error.message : String(error),
          quantity: cartItem.quantity,
          option: cartItem.option,
        });
        return acc;
      }
    },
    {
      itemsToAdd: [] as Array<{
        cartItem: CartItem;
        itemForCart: CartItemToAdd;
        product: Product;
      }>,
      failedItems: [] as FailedCartItem[],
    }
  );

  processingResults.itemsToAdd.forEach(({ itemForCart }) => {
    cart.addItem(dispensary, itemForCart);
    restoredCount += 1;
  });

  failedCount = processingResults.failedItems.length;
  failedItems.push(...processingResults.failedItems);

  if (failedItems.length > 0) {
    console.warn('Failed to restore these items:', failedItems);
  }

  return {
    restoredCount,
    failedCount,
    failedItems,
  };
}

function getProductName(id: string, products?: Products[]): string {
  if (!products) {
    return '';
  }
  const product = products.find((p) => p.id === id);
  return product?.name ?? '';
}
